import type { RollCallStatus, Student } from '@cdf/api-client-react';

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export type VoiceIntent = 'present' | 'absent' | 'undo' | 'none';

/**
 * A generic roster item that the voice matcher can score.
 * In roll-call mode this is a RollCallEntry; in sign-out mode it is an
 * AttendanceRecord.  Both carry a nested `student` with first name / last
 * initial, plus a status that determines whether the student is already at
 * the target state.
 */
export interface VoiceRosterItem {
  id: string;
  student?: Pick<Student, 'firstName' | 'lastInitial' | 'group'> | null;
}

export interface VoiceMatch<T extends VoiceRosterItem = VoiceRosterItem> {
  /** The matched roster item */
  record: T;
  /** 0–1 confidence score */
  confidence: number;
  /** Student display label e.g. "Oliver B." */
  label: string;
}

export interface VoiceParseResult<T extends VoiceRosterItem = VoiceRosterItem> {
  intent: VoiceIntent;
  /** "undo" has no match; "none" intent means no name was recognised */
  match: VoiceMatch<T> | null;
  /** When two or more students are equally close, caller must disambiguate */
  ambiguous: VoiceMatch<T>[];
  /** Cleaned transcript for display */
  transcript: string;
}

/* ------------------------------------------------------------------ *
 * Text normalisation
 * ------------------------------------------------------------------ */

function normalize(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z\s]/g, '') // strip punctuation / digits
    .replace(/\s+/g, ' ');
}

/**
 * Lightweight Soundex-style phonetic code (first letter + 3 digits).
 * Good enough for name confusion (Oliver / Olliver / Olivir).
 */
function soundex(word: string): string {
  if (!word) return '';
  const firstChar = word[0];
  if (!firstChar) return '';
  const c = firstChar.toUpperCase();
  const codes: Record<string, string> = {
    b: '1', f: '1', p: '1', v: '1',
    c: '2', g: '2', j: '2', k: '2', q: '2', s: '2', x: '2', z: '2',
    d: '3', t: '3',
    l: '4',
    m: '5', n: '5',
    r: '6'
  };
  let result = c;
  let prev = codes[firstChar.toLowerCase()] ?? '0';
  for (let i = 1; i < word.length && result.length < 4; i++) {
    const ch = word[i];
    if (!ch) continue;
    const code = codes[ch.toLowerCase()] ?? '0';
    if (code === '0') {
      prev = '0';
      continue;
    }
    if (code !== prev) {
      result += code;
    }
    prev = code;
  }
  while (result.length < 4) result += '0';
  return result;
}

/**
 * Levenshtein edit distance (iterative, O(n*m)).
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Similarity score 0–1 based on edit distance relative to the longer string.
 */
function similarity(a: string, b: string): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const dist = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);
  return 1 - dist / maxLen;
}

/* ------------------------------------------------------------------ *
 * Intent parsing
 * ------------------------------------------------------------------ */

const PRESENT_WORDS = ['here', 'present', 'arrived', 'arrived', 'in', 'yes', 'yep', 'yeah'];
const ABSENT_WORDS = ['absent', 'away', 'not here', 'missing', 'gone', 'no', 'nope', 'sick'];
const UNDO_WORDS = ['undo', 'revert', 'cancel last', 'take back'];

/* ------------------------------------------------------------------ *
 * Confirmation parsing (two-step roll-call confirmation flow)
 * ------------------------------------------------------------------ */

/** Words the teacher can say to confirm a pending selection. */
export const CONFIRM_WORDS = ['ok', 'okay', 'yes', 'yep', 'confirm', 'correct', 'here', 'present'];
/** Words the teacher can say to cancel a pending selection. */
export const CANCEL_WORDS = ['no', 'nope', 'cancel', 'skip', 'wrong'];

/**
 * Filler words that may accompany a confirm/cancel phrase without changing
 * its meaning ("yes please", "ok then", "no wait").  These are ignored when
 * deciding whether a transcript is a pure confirmation/cancel command.
 */
const FILLER_WORDS = new Set([
  'is', 'she', 'he', 'the', 'a', 'an', 'that', 'this', 'it', 'please',
  'then', 'now', 'well', 'so', 'yeah', 'uh', 'um', 'just', 'like', 'there',
  'wait', 'go', 'ahead'
]);

const CONFIRM_SET = new Set(CONFIRM_WORDS);
const CANCEL_SET = new Set(CANCEL_WORDS);

/**
 * Parse a transcript for a confirmation or cancel command.
 *
 * Returns `'confirm'` or `'cancel'` only when **every** meaningful token in
 * the transcript is a confirm, cancel, or filler word.  If the transcript
 * contains any other token (e.g. a student name like "Emma here"), this
 * returns `null` so the caller can attempt a roster-name match first —
 * that way "Emma here" switches the pending selection to Emma instead of
 * confirming the previously selected student.
 *
 * Matching is done on whole tokens so that "ok" doesn't match inside "token".
 *
 * @returns `'confirm'` | `'cancel'` | `null` (not a pure confirmation phrase)
 */
export function parseConfirmationCommand(transcript: string): 'confirm' | 'cancel' | null {
  const tokens = normalize(transcript).split(' ').filter(Boolean);
  if (tokens.length === 0) return null;

  let hasConfirm = false;
  let hasCancel = false;

  for (const token of tokens) {
    if (CONFIRM_SET.has(token)) {
      hasConfirm = true;
    } else if (CANCEL_SET.has(token)) {
      hasCancel = true;
    } else if (!FILLER_WORDS.has(token)) {
      // Contains a non-confirm/cancel/filler word (likely a name) — not a pure command
      return null;
    }
  }

  if (hasConfirm) return 'confirm';
  if (hasCancel) return 'cancel';
  return null;
}

function extractIntent(words: string[]): { intent: VoiceIntent; remaining: string[] } {
  const joined = words.join(' ');

  // Undo takes priority — it's a command, not a name match
  for (const u of UNDO_WORDS) {
    if (joined.includes(u)) {
      return { intent: 'undo', remaining: [] };
    }
  }

  // Check for absent markers
  for (const a of ABSENT_WORDS) {
    if (joined.includes(a)) {
      return { intent: 'absent', remaining: words.filter((w) => !a.split(' ').includes(w)) };
    }
  }

  // Check for present markers
  for (const p of PRESENT_WORDS) {
    if (joined.includes(p)) {
      return { intent: 'present', remaining: words.filter((w) => !p.split(' ').includes(w)) };
    }
  }

  return { intent: 'none', remaining: words };
}

/* ------------------------------------------------------------------ *
 * Name matching
 * ------------------------------------------------------------------ */

const CONFIDENCE_THRESHOLD = 0.55;
const AMBIGUITY_DELTA = 0.08; // matches within this distance of the top are ambiguous

/**
 * Score a transcript against a student name (first name + last initial).
 * Combines token similarity with phonetic match.
 */
function scoreName(transcript: string, firstName: string, lastInitial: string): number {
  const tokens = transcript.split(' ').filter(Boolean);
  const firstLower = firstName.toLowerCase();
  const lastLower = lastInitial.toLowerCase();

  let bestFirst = 0;
  let bestLast = 0;

  for (const token of tokens) {
    const simFirst = similarity(token, firstLower);
    // Phonetic match boost: same soundex = high confidence even with spelling diffs
    const phonFirst = soundex(token) === soundex(firstLower) ? 0.7 : 0;
    bestFirst = Math.max(bestFirst, Math.max(simFirst, phonFirst));

    // Last initial: single-letter match is easy; also check full token vs initial
    if (lastLower && token.length >= 1) {
      const simLast = similarity(token, lastLower);
      const tokenStartsLast = token[0] === lastLower[0] ? 0.6 : 0;
      bestLast = Math.max(bestLast, Math.max(simLast, tokenStartsLast));
    }
  }

  // Weight: first name is the primary signal (0.8), last initial is secondary (0.2)
  // If no last initial exists, weight fully on first name
  const firstWeight = lastLower ? 0.8 : 1.0;
  const lastWeight = lastLower ? 0.2 : 0;
  return bestFirst * firstWeight + bestLast * lastWeight;
}

/**
 * Parse a voice transcript against the event roster.
 *
 * @param transcript Raw or final transcript from the speech recogniser
 * @param roster Current attendance records (each has `.student`)
 * @param currentStatus If provided, in roll-call mode skip students already at
 *   the target status to reduce false double-marks. Pass undefined to match all.
 */
export function parseVoiceCommand<T extends VoiceRosterItem>(
  transcript: string,
  roster: T[]
): VoiceParseResult<T> {
  const cleaned = normalize(transcript);
  const words = cleaned.split(' ').filter(Boolean);

  if (words.length === 0) {
    return { intent: 'none', match: null, ambiguous: [], transcript: cleaned };
  }

  const { intent, remaining } = extractIntent(words);

  if (intent === 'undo') {
    return { intent: 'undo', match: null, ambiguous: [], transcript: cleaned };
  }

  // For matching, use remaining words after stripping intent markers.
  // If no intent word was found, use all words (bare name = "present" in roll call,
  // but the caller decides default intent).
  const nameWords = remaining.length > 0 ? remaining : words;
  const nameTranscript = nameWords.join(' ');

  if (!nameTranscript) {
    return { intent, match: null, ambiguous: [], transcript: cleaned };
  }

  // Score every rostered student
  const scored = roster
    .map((record) => {
      const student = record.student;
      if (!student) return null;
      const score = scoreName(nameTranscript, student.firstName, student.lastInitial);
      return {
        record,
        confidence: score,
        label: `${student.firstName} ${student.lastInitial}.`
      } satisfies VoiceMatch<T>;
    })
    .filter((m): m is VoiceMatch<T> => m !== null && m.confidence >= CONFIDENCE_THRESHOLD)
    .sort((a, b) => b.confidence - a.confidence);

  if (scored.length === 0) {
    return { intent, match: null, ambiguous: [], transcript: cleaned };
  }

  const best = scored[0];
  const second = scored[1];

  // Check ambiguity: if the 2nd-best is within AMBIGUITY_DELTA of the best, it's ambiguous
  if (best && second && best.confidence - second.confidence <= AMBIGUITY_DELTA) {
    const ambiguous = scored
      .filter((m) => best.confidence - m.confidence <= AMBIGUITY_DELTA)
      .slice(0, 4);
    return { intent, match: null, ambiguous, transcript: cleaned };
  }

  return { intent, match: best ?? null, ambiguous: [], transcript: cleaned };
}

/**
 * Default intent when a name is spoken with no explicit marker word.
 * In roll-call mode a bare name = present; in sign-out mode = open confirmation.
 */
export function defaultIntentForMode(mode: 'roll_call' | 'sign_out'): VoiceIntent {
  return mode === 'roll_call' ? 'present' : 'none';
}

/**
 * Map a voice intent to a roll-call status.
 */
export function intentToRollCallStatus(intent: VoiceIntent): RollCallStatus | null {
  if (intent === 'present') return 'present';
  if (intent === 'absent') return 'absent';
  return null;
}
